import {
  Accordion,
  Badge,
  Box,
  Button,
  Center,
  Group,
  LoadingOverlay,
  Stack,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { FirebaseError } from 'firebase/app';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ListRowSkeleton } from '@/components';
import { useActiveRoutes, useAuth, useDailyAssignments } from '@/hooks';
import { claimRoute, selectExtra, unassignRoute, withdrawExtra } from '@/services';
import type { RouteCategory, RouteDefinition } from '@/types';
import { formatIsoDate, todayIsoDate } from '@/utils';
import { logStartupTiming, STARTUP_STARTED_AT } from '@/utils/startupPerformance';

export const HomePage = () => {
  const { t } = useTranslation();
  const { appUser, loading: authLoading } = useAuth();
  const today = todayIsoDate();
  const { routes, loading: routesLoading } = useActiveRoutes();
  const { assignments, slotsByRouteId, loading: assignmentsLoading } = useDailyAssignments(today);
  const routeCategories: readonly RouteCategory[] = ['pakkeshop', 'collection', 'extra'];
  const [pendingRouteId, setPendingRouteId] = useState<string | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<RouteCategory[]>([
    ...routeCategories,
  ]);
  const initialDataSettledLogged = useRef(false);

  const loading = routesLoading || assignmentsLoading || authLoading;
  useEffect(() => {
    if (loading || initialDataSettledLogged.current) return;
    logStartupTiming('home-data-settled', STARTUP_STARTED_AT);
    initialDataSettledLogged.current = true;
  }, [loading]);

  const isRouteCategory = (value: string): value is RouteCategory =>
    routeCategories.some((category) => category === value);
  const getRouteName = (route: RouteDefinition) =>
    route.category === 'extra'
      ? t('routeCategories.extraHours', { hours: route.extraDurationHours })
      : route.name;
  const takenCount = routes.filter((route) =>
    route.category === 'extra'
      ? assignments.some(
          (assignment) =>
            assignment.kind === 'extra' &&
            assignment.routeId === route.id &&
            assignment.status === 'assigned',
        )
      : Boolean(slotsByRouteId[route.id]?.assignedTo),
  ).length;

  const functionsErrorMessages: Record<string, string> = {
    'already-exists': t('home.errors.alreadyTaken'),
    'failed-precondition': t('home.errors.noLongerAvailable'),
    'permission-denied': t('home.errors.noLongerHeld'),
  };

  const showActionError = (error: unknown) => {
    const code = error instanceof FirebaseError ? error.code.replace('functions/', '') : undefined;
    notifications.show({
      color: 'red',
      message: (code && functionsErrorMessages[code]) ?? t('home.errors.generic'),
    });
  };

  const handleClaim = async (routeId: string) => {
    setPendingRouteId(routeId);
    try {
      const route = routes.find((r) => r.id === routeId);
      await claimRoute(routeId);
      notifications.show({
        color: 'signal',
        message: t('home.notifications.assigned', {
          name: route?.name ?? t('home.notifications.route'),
        }),
      });
    } catch (error) {
      showActionError(error);
    } finally {
      setPendingRouteId(null);
    }
  };

  const handleUnassign = async (routeId: string) => {
    setPendingRouteId(routeId);
    try {
      const route = routes.find((r) => r.id === routeId);
      await unassignRoute(routeId);
      notifications.show({
        color: 'signal',
        message: t('home.notifications.unassigned', {
          name: route?.name ?? t('home.notifications.route'),
        }),
      });
    } catch (error) {
      showActionError(error);
    } finally {
      setPendingRouteId(null);
    }
  };

  const handleSelectExtra = async (routeId: string) => {
    setPendingRouteId(routeId);
    try {
      const route = routes.find((item) => item.id === routeId);
      await selectExtra(routeId);
      notifications.show({
        color: 'signal',
        message: t('home.notifications.extraSelected', {
          name: route?.extraDurationHours
            ? t('routeCategories.extraHours', { hours: route.extraDurationHours })
            : t('routeCategories.extra'),
        }),
      });
    } catch (error) {
      showActionError(error);
    } finally {
      setPendingRouteId(null);
    }
  };

  const handleWithdrawExtra = async (assignmentId: string) => {
    setPendingRouteId(assignmentId);
    try {
      await withdrawExtra(assignmentId);
      notifications.show({
        color: 'signal',
        message: t('home.notifications.extraWithdrawn'),
      });
    } catch (error) {
      showActionError(error);
    } finally {
      setPendingRouteId(null);
    }
  };

  return (
    <Box pos="relative">
      <LoadingOverlay overlayProps={{ blur: 1 }} visible={pendingRouteId !== null} zIndex={1000} />
      <Box px="md" py="lg" style={{ borderBottom: '1px solid var(--mantine-color-ink-3)' }}>
        <Group align="baseline" justify="space-between">
          <Box>
            <Text
              c="signal.7"
              ff="monospace"
              fw={700}
              mb={2}
              size="xs"
              style={{ letterSpacing: '0.24em' }}
            >
              {t('home.manifestToday')}
            </Text>
            <Text ff="monospace" fw={600} size="sm" style={{ letterSpacing: '0.04em' }}>
              {formatIsoDate(today).toUpperCase()}
            </Text>
          </Box>
          {!loading && (
            <Text c="ink.7" ff="monospace" size="xs">
              {t('home.assignedCount', { taken: takenCount, total: routes.length })}
            </Text>
          )}
        </Group>
      </Box>

      {loading ? (
        <ListRowSkeleton />
      ) : routes.length === 0 ? (
        <Center px="md" py={80}>
          <Text c="ink.7" ta="center">
            {t('home.empty')}
          </Text>
        </Center>
      ) : (
        <Stack gap={0}>
          <Accordion
            chevronPosition="right"
            multiple
            onChange={(values) =>
              setExpandedCategories(
                values.filter((value): value is RouteCategory => isRouteCategory(value)),
              )
            }
            styles={{
              chevron: { color: 'var(--mantine-color-ink-6)' },
              control: {
                borderBottom: '1px solid var(--mantine-color-ink-3)',
                padding: '12px 16px',
              },
              item: { border: 0 },
              panel: { padding: 0 },
            }}
            value={expandedCategories}
          >
            {routeCategories.map((category) => {
              const categoryRoutes = routes
                .filter((route) => route.category === category)
                .sort((a, b) =>
                  getRouteName(a).localeCompare(getRouteName(b), undefined, {
                    sensitivity: 'base',
                  }),
                );
              if (categoryRoutes.length === 0) return null;

              return (
                <Accordion.Item key={category} value={category}>
                  <Accordion.Control>
                    <Group justify="space-between" pr="md">
                      <Text c="signal.7" ff="monospace" fw={700} size="xs">
                        {t(`routeCategories.${category}`)}
                      </Text>
                      <Text c="ink.6" ff="monospace" size="xs">
                        {categoryRoutes.length}
                      </Text>
                    </Group>
                  </Accordion.Control>
                  <Accordion.Panel>
                    {categoryRoutes.map((route) => {
                      const slot = slotsByRouteId[route.id];
                      const isExtra = route.category === 'extra';
                      const extraSelections = assignments.filter(
                        (assignment) =>
                          assignment.kind === 'extra' &&
                          assignment.routeId === route.id &&
                          assignment.status === 'assigned',
                      );
                      const myExtraSelections = extraSelections.filter(
                        (assignment) => assignment.userId === appUser?.uid,
                      );
                      const isTaken = Boolean(slot?.assignedTo);
                      const isMine = Boolean(slot?.assignedTo) && slot?.assignedTo === appUser?.uid;
                      const isPending = pendingRouteId === route.id;
                      const routeName = getRouteName(route);

                      return (
                        <Box
                          key={route.id}
                          px="md"
                          py="md"
                          style={{
                            borderBottom: '1px solid var(--mantine-color-ink-3)',
                            opacity: isTaken && !isMine && !isExtra ? 0.55 : 1,
                          }}
                        >
                          <Group gap={8} wrap="nowrap">
                            <Text fw={700} lineClamp={1} size="md" style={{ minWidth: 0 }}>
                              {routeName}
                            </Text>
                            <Badge color={isExtra ? 'orange' : 'gray'} radius={0} variant="outline">
                              {t(`routeCategories.${route.category}`)}
                            </Badge>
                          </Group>
                          {isExtra ? (
                            <Group gap="xs" mt={6}>
                              <Text c="ink.8" ff="monospace" size="xs">
                                {t('home.extraHours', { hours: route.extraDurationHours })}
                              </Text>
                              <Text c="ink.5">·</Text>
                              <Text c="ink.8" ff="monospace" size="xs">
                                {t('home.extraSelections', { count: extraSelections.length })}
                              </Text>
                            </Group>
                          ) : (
                            <Group gap="xs" mt={6} wrap="wrap">
                              <Text c="ink.8" ff="monospace" size="xs">
                                {route.startTime}–{route.endTime}
                              </Text>
                              <Text c="ink.5">·</Text>
                              <Text c="ink.8" ff="monospace" size="xs">
                                {route.distanceKm.toFixed(1)} {t('home.km')}
                              </Text>
                            </Group>
                          )}
                          {!isExtra && isTaken && (
                            <Text
                              c={isMine ? 'signal.7' : 'ink.7'}
                              ff="monospace"
                              fw={600}
                              mt={6}
                              size="10px"
                              style={{ letterSpacing: '0.08em' }}
                            >
                              {isMine ? t('home.assignedToYou') : t('home.assigned')}
                            </Text>
                          )}

                          {isExtra && myExtraSelections.length > 0 && (
                            <Stack gap={4} mt="sm">
                              {myExtraSelections.map((selection) => (
                                <Group justify="space-between" key={selection.id}>
                                  <Text c="signal.7" ff="monospace" size="xs">
                                    {t('home.extraSelected')}
                                  </Text>
                                  <Button
                                    color="red"
                                    loading={pendingRouteId === selection.id}
                                    onClick={() => handleWithdrawExtra(selection.id)}
                                    size="xs"
                                    variant="subtle"
                                  >
                                    {t('home.withdrawExtra')}
                                  </Button>
                                </Group>
                              ))}
                            </Stack>
                          )}

                          <Group justify="flex-start" mt="xl">
                            {isExtra ? (
                              <Button
                                loading={isPending}
                                onClick={() => handleSelectExtra(route.id)}
                                size="xs"
                                style={{ letterSpacing: '0.06em', fontWeight: 700 }}
                              >
                                {t('home.addExtra')}
                              </Button>
                            ) : isMine ? (
                              <Button
                                color="red"
                                loading={isPending}
                                onClick={() => handleUnassign(route.id)}
                                size="xs"
                                style={{ letterSpacing: '0.06em', fontWeight: 700 }}
                                variant="outline"
                              >
                                {t('home.unassign')}
                              </Button>
                            ) : (
                              <Button
                                disabled={isTaken}
                                loading={isPending}
                                onClick={() => handleClaim(route.id)}
                                size="xs"
                                style={{ letterSpacing: '0.06em', fontWeight: 700 }}
                              >
                                {t('home.assign')}
                              </Button>
                            )}
                          </Group>
                        </Box>
                      );
                    })}
                  </Accordion.Panel>
                </Accordion.Item>
              );
            })}
          </Accordion>
        </Stack>
      )}
    </Box>
  );
};
